import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { PlusIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import {
  BankConnectionReturn,
  BankConnectionUpdate,
} from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canManageBankConnections } from './constants/UserTypes';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Description,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { Switch } from './common/Catalyst/switch';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankConnectionError as extractError } from './bank/linear/bankConnectionMeta';
import { CreateBankConnectionModal } from './bank/linear/CreateBankConnectionModal';

interface AlertState {
  type: 'error' | 'success';
  title: string;
  message: string;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

interface BankConnectionListProps {
  companyId: string | null;
  /** Another company's connections, seen by a SuperAdmin (#400): no create,
   *  edit or delete. */
  readOnly?: boolean;
}

const BankConnectionList: React.FC<BankConnectionListProps> = ({
  companyId,
  readOnly = false,
}) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canManage = useMemo(
    () => canManageBankConnections(userType) && !readOnly,
    [userType, readOnly]
  );

  const [connections, setConnections] = useState<BankConnectionReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchAlert, setFetchAlert] = useState<AlertState | null>(null);

  // Create: the Linear-style modal (#665)
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<BankConnectionReturn | null>(
    null
  );
  const [editForm, setEditForm] = useState({
    label: '',
    client_id: '',
    is_active: true,
    client_secret: '',
    lockbox_company_id: '',
  });
  const [editError, setEditError] = useState<AlertState | null>(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] =
    useState<BankConnectionReturn | null>(null);
  const [deleteError, setDeleteError] = useState<AlertState | null>(null);

  const fetchConnections = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await bankService.listCompanyConnections({ companyId });
      setConnections(response.data);
      setFetchAlert(null);
    } catch (err) {
      console.error('Error fetching bank connections:', err);
      const { title, message } = extractError(err);
      setFetchAlert({ type: 'error', title, message });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // --- Edit -----------------------------------------------------------------

  const openEdit = (conn: BankConnectionReturn) => {
    setEditTarget(conn);
    setEditForm({
      label: conn.label ?? '',
      client_id: conn.client_id,
      is_active: conn.is_active,
      client_secret: '',
      lockbox_company_id: conn.lockbox_company_id ?? '',
    });
    setEditError(null);
  };

  const closeEdit = () => {
    if (submitting) return;
    setEditTarget(null);
  };

  const handleEdit = async () => {
    if (!editTarget) return;

    // Only send fields that changed. client_secret is only sent when the user
    // typed a new one (blank = leave the stored secret untouched).
    const update: BankConnectionUpdate = {};
    if (editForm.label !== (editTarget.label ?? '')) {
      update.label = editForm.label.trim() || null;
    }
    if (editForm.client_id.trim() !== editTarget.client_id) {
      update.client_id = editForm.client_id.trim();
    }
    if (editForm.is_active !== editTarget.is_active) {
      update.is_active = editForm.is_active;
    }
    if (editForm.client_secret.trim() !== '') {
      update.client_secret = editForm.client_secret;
    }
    if (
      editForm.lockbox_company_id.trim() !==
      (editTarget.lockbox_company_id ?? '')
    ) {
      update.lockbox_company_id =
        editForm.lockbox_company_id.trim() || null;
    }

    if (Object.keys(update).length === 0) {
      setEditTarget(null);
      return;
    }

    try {
      setSubmitting(true);
      setEditError(null);
      // Changing client_id or client_secret re-verifies against the bank.
      await bankService.updateBankConnection({
        id: editTarget.id,
        bankConnectionUpdate: update,
      });
      setEditTarget(null);
      await fetchConnections();
    } catch (err) {
      console.error('Error updating bank connection:', err);
      setEditError({ type: 'error', ...extractError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Delete ---------------------------------------------------------------

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSubmitting(true);
      setDeleteError(null);
      await bankService.deleteBankConnection({ id: deleteTarget.id });
      setDeleteTarget(null);
      await fetchConnections();
    } catch (err) {
      console.error('Error deleting bank connection:', err);
      setDeleteError({ type: 'error', ...extractError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render ---------------------------------------------------------------

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
        <Heading level={4}>Bank Connections</Heading>
        {canManage && (
          <Button color="sky" onClick={() => setIsCreateOpen(true)}>
            <PlusIcon className="h-5 w-5 mr-2" />
            New Connection
          </Button>
        )}
      </div>

      <Text className="mb-4">
        A connection stores a company's credentials for a bank. The secret is
        encrypted and never shown again. Creating or rotating a credential
        verifies it against the bank first, so it only succeeds from an
        environment whitelisted for bank access.
      </Text>

      {fetchAlert && (
        <ShowAlert
          alertType={fetchAlert.type}
          alertTitle={fetchAlert.title}
          alertMessage={fetchAlert.message}
          isVisible={!!fetchAlert}
          onClose={() => setFetchAlert(null)}
          timeout={8000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {connections.length === 0 ? (
            <Text>
              No bank connections yet.
              {canManage
                ? ' Use “New Connection” to add one.'
                : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Label
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Provider
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Client ID
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Lockbox ID
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Created
                        </TableHeader>
                        {canManage && (
                          <TableHeader className="text-base font-semibold">
                            <span className="sr-only">Actions</span>
                          </TableHeader>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {connections.map((conn) => (
                        <TableRow key={conn.id}>
                          <TableCell>{conn.label || '-'}</TableCell>
                          <TableCell>{conn.provider_key}</TableCell>
                          <TableCell>{conn.client_id}</TableCell>
                          <TableCell>
                            {conn.lockbox_company_id || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge color={conn.is_active ? 'lime' : 'zinc'}>
                              {conn.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(conn.date_created)}</TableCell>
                          {canManage && (
                            <TableCell>
                              <Dropdown>
                                <DropdownButton plain aria-label="Actions">
                                  <EllipsisHorizontalIcon className="h-5 w-5" />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                  <DropdownItem onClick={() => openEdit(conn)}>
                                    Edit
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      setDeleteError(null);
                                      setDeleteTarget(conn);
                                    }}
                                  >
                                    Delete
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create: verifies against the live bank, never retries (#665) */}
      <CreateBankConnectionModal
        open={isCreateOpen}
        companyId={companyId}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => fetchConnections()}
      />

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={closeEdit}>
        <DialogTitle>Edit Bank Connection</DialogTitle>
        <DialogDescription>
          Leave the secret blank to keep the current one. Entering a new secret
          rotates it and re-verifies against the bank.
        </DialogDescription>
        <DialogBody>
          {editError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={editError.title}
                alertMessage={editError.message}
                isVisible={!!editError}
                onClose={() => setEditError(null)}
                timeout={12000}
                isInline={true}
              />
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Label</Label>
                <Input
                  name="label"
                  value={editForm.label}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, label: e.target.value }))
                  }
                />
              </Field>
              <Field>
                <Label>Client ID</Label>
                <Input
                  name="client_id"
                  value={editForm.client_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, client_id: e.target.value }))
                  }
                />
              </Field>
              <Field>
                <Label>Lockbox Company ID</Label>
                <Description>
                  The bank-assigned Lockbox Management CO ID, used as Column A of the payer file. Optional — add it once the bank provides it; payer files cannot be submitted until it is set.
                </Description>
                <Input
                  name="lockbox_company_id"
                  value={editForm.lockbox_company_id}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      lockbox_company_id: e.target.value,
                    }))
                  }
                  placeholder="e.g. 9998"
                />
              </Field>
              <Field>
                <Label>New Client Secret</Label>
                <Description>Leave blank to keep the current secret (no need to remove the placeholder text).</Description>
                <Input
                  type="password"
                  name="client_secret"
                  autoComplete="new-password"
                  value={editForm.client_secret}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      client_secret: e.target.value,
                    }))
                  }
                  placeholder="Enter a new secret to rotate it"
                />
              </Field>
              <Field className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <Description>
                    Inactive connections are ignored when loading accounts.
                  </Description>
                </div>
                <Switch
                  checked={editForm.is_active}
                  onChange={(checked) =>
                    setEditForm((f) => ({ ...f, is_active: checked }))
                  }
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={closeEdit} disabled={submitting}>
            Cancel
          </Button>
          <Button color="sky" onClick={handleEdit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !submitting && setDeleteTarget(null)}>
        <DialogTitle>Delete Bank Connection</DialogTitle>
        <DialogDescription>
          {deleteTarget?.label
            ? `Delete “${deleteTarget.label}”? `
            : 'Delete this connection? '}
          It will be deactivated. Any HOA accounts assigned to it must be
          unassigned first.
        </DialogDescription>
        <DialogBody>
          {deleteError && (
            <ShowAlert
              alertType="error"
              alertTitle={deleteError.title}
              alertMessage={deleteError.message}
              isVisible={!!deleteError}
              onClose={() => setDeleteError(null)}
              timeout={12000}
              isInline={true}
            />
          )}
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setDeleteTarget(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BankConnectionList;
